import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CityCombobox } from "@/components/address/city-combobox";
import { OrderItemsEditor } from "@/components/wholesale/order-items-editor";
import { TotalsPanel } from "@/components/wholesale/totals-panel";
import { EMPTY_SHIPPING, SHIPPING_FIELDS, shippingFromPartner } from "@/components/wholesale/constants";
import {
  usePartners, useCreateWholesaleOrder, useWholesaleContext, type PartnerListItem,
} from "@/hooks/use-wholesale";
import { useDebounce } from "@/hooks/use-debounce";
import { editorTotals, toItemInputs, type EditorItem } from "@/lib/wholesale-items";
import { fmtPct } from "@/lib/wholesale-format";

export function NewWholesaleOrderPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search);
  const partners = usePartners(debounced);
  const ctx = useWholesaleContext();
  const createOrder = useCreateWholesaleOrder();

  const [partner, setPartner] = useState<PartnerListItem | null>(null);
  const [shipping, setShipping] = useState({ ...EMPTY_SHIPPING });
  const [note, setNote] = useState("");
  const [customerNote, setCustomerNote] = useState("");
  const [items, setItems] = useState<EditorItem[]>([]);

  const vatRate = ctx.data?.vatRate;
  const totals = editorTotals(items, vatRate ?? 0);

  function pickPartner(p: PartnerListItem) {
    setPartner(p);
    setShipping(shippingFromPartner(p));
  }

  async function handleSave() {
    if (!partner) {
      toast.error("Odaberi partnera");
      return;
    }
    try {
      const order = await createOrder.mutateAsync({
        partnerId: partner.id,
        shipping,
        note: note.trim(),
        customerNote: customerNote.trim(),
        items: toItemInputs(items),
      });
      toast.success("Nacrt narudžbe sačuvan");
      navigate({ to: "/wholesale/orders/$orderId", params: { orderId: order.id } });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri spremanju narudžbe");
    }
  }

  const list = partners.data ?? [];

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-xs" aria-label="Nazad na narudžbe" asChild>
          <Link to="/wholesale/orders">
            <ArrowLeft className="h-3.5 w-3.5" />
          </Link>
        </Button>
        <h2 className="text-sm font-medium">Nova narudžba</h2>
      </div>

      <div className="flex flex-col gap-2 ring-1 ring-foreground/10 p-3">
        <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Partner</p>
        {partner ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium">{partner.name}</span>
            <Badge variant="outline">Rabat {fmtPct(partner.discountPct)}</Badge>
            <span className="text-xs text-muted-foreground">Rok plaćanja {partner.paymentTermDays} dana</span>
            <Button variant="outline" size="xs" onClick={() => setPartner(null)}>Promijeni</Button>
          </div>
        ) : (
          <>
            <div className="relative max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Label htmlFor="new-order-partner-search" className="sr-only">Pretraži partnere</Label>
              <Input
                id="new-order-partner-search"
                className="h-8 text-xs pl-8"
                placeholder="Pretraži partnere…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {partners.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : partners.error ? (
              <p className="text-xs text-destructive">
                Partneri nisu učitani: {(partners.error as Error).message}
              </p>
            ) : list.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nema partnera.</p>
            ) : (
              <div className="flex flex-col divide-y ring-1 ring-foreground/10 max-h-56 overflow-y-auto">
                {list.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickPartner(p)}
                    className="flex items-center gap-2 p-2 text-left text-xs hover:bg-muted/50 transition-colors"
                  >
                    <span className="font-medium flex-1 truncate">{p.name}</span>
                    <span className="text-muted-foreground truncate">{p.email || p.phone || "—"}</span>
                    <Badge variant="outline" className="shrink-0">{fmtPct(p.discountPct)}</Badge>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {partner && (
        <>
          <div className="flex flex-col gap-2 ring-1 ring-foreground/10 p-3">
            <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Adresa dostave</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {SHIPPING_FIELDS.map(([key, label]) => (
                <div key={key} className="flex flex-col gap-1">
                  <Label htmlFor={`new-order-shipping-${key}`} className="text-xs">{label}</Label>
                  {key === "city" ? (
                    <CityCombobox
                      id={`new-order-shipping-${key}`}
                      city={shipping.city}
                      postalCode={shipping.postalCode}
                      onSelect={(city, postalCode) => setShipping((s) => ({ ...s, city, postalCode }))}
                    />
                  ) : (
                    <Input
                      id={`new-order-shipping-${key}`}
                      className="h-8 text-xs"
                      value={shipping[key]}
                      onChange={(e) => setShipping((s) => ({ ...s, [key]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-order-note" className="text-xs">Interna napomena</Label>
              <Textarea id="new-order-note" className="text-xs min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-order-customer-note" className="text-xs">Napomena za otpremnicu</Label>
              <Textarea
                id="new-order-customer-note"
                className="text-xs min-h-16"
                value={customerNote}
                onChange={(e) => setCustomerNote(e.target.value)}
              />
            </div>
          </div>

          <OrderItemsEditor
            items={items}
            partnerDiscount={partner.discountPct ?? 0}
            storeDiscounts={partner.storeDiscounts}
            onChange={setItems}
          />

          <TotalsPanel totals={totals} vatRate={vatRate} />

          <div className="flex justify-end">
            <Button size="sm" onClick={handleSave} disabled={createOrder.isPending}>
              {createOrder.isPending ? "Spremanje…" : "Sačuvaj nacrt"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
